import { useEffect, useMemo } from "react";
import type { ComponentType, ReactNode } from "react";
import { Icon } from "../Icons/Icon";
import { Spinner } from "../Feedback/Spinner";
import { Tooltip } from "../Feedback/Tooltip";
import { CopyableValue } from "../Display/CopyableValue";
import { CopyableProvider } from "../Display/Internal/CopyableContext";
import { BadgeSizeContext } from "../Display/BadgeSizeContext";
import {
  stringToColorPair,
  stringToColorPairDark,
} from "../Display/stringToColor";
import { useThemeContext } from "../Theme/useThemeContext";
import { headingFontStyle } from "../Theme/headingFontStyle";
import { usePageChrome, type PageDensity } from "./PageChromeProvider";
import { devSlotNumber } from "../Helpers/devSlot";
import { useMinWidth } from "../Helpers/useMinWidth";
import { ControlBar } from "./ControlBar";
import { PageEdgeContext } from "./Internal/PageEdgeContext";
import { formatStatValue } from "./Helpers/formatStatValue";
import { StatValueLink_ } from "./Internal/StatValueLink";
import type { StatEntry } from "./Types/StatItemType";
import { isStatDivider } from "./Types/StatItemType";

interface PageProps {
  /** Icon shown at the start of the title row. Pass a Phosphor icon component reference. */
  icon?: ComponentType<{ className?: string }> | undefined;
  /** String used to derive the icon's background/foreground color. Use the page type name (e.g. "shipment", "company") so all pages of the same type share a color. */
  iconColorKey?: string | undefined;
  /** Explicit icon colors. Overrides iconColorKey when set. */
  iconColor?: { background: string; foreground: string } | undefined;
  /**
   * Custom visual rendered at the start of the title row, replacing the icon
   * box entirely. When set, `icon`/`iconColorKey`/`iconColor` are ignored. Use
   * for brand marks like a carrier logo. A background-refresh spinner is
   * overlaid in the corner when `refreshing` is true.
   */
  leadingVisual?: ReactNode;
  /** Page title rendered as h1 in the header. */
  title?: ReactNode;
  /**
   * When true, the title yields space on a cramped header: its font steps down
   * on smaller viewports and long titles wrap onto a second line instead of
   * holding a fixed width. Use on entity pages whose header actions must stay
   * visible (e.g. the shipment view) rather than folding into the kebab.
   * Default (false) keeps the title on one line at full size.
   */
  titleWraps?: boolean | undefined;
  /** Explicit document title. Falls back to `title` if not set. */
  pageTitle?: string | undefined;
  /** Badge/emoji shown next to the title. */
  titleBadge?: ReactNode;
  /** Subtitle shown below the title. */
  description?: ReactNode;
  /** Badge bar rendered between the stats bar and the content area. Used for flags, labels, and problems. */
  tags?: ReactNode;
  /** Custom content rendered in the middle of the header, separated from the title. */
  headerContent?: ReactNode;
  /** Compact stats shown inline in the header, to the left of controls. */
  headerStats?: StatEntry[] | undefined;
  /** Labeled filter controls (ControlBarItem wrappers) in the header. */
  headerFilters?: ReactNode;
  /** Action buttons in the header. */
  headerActions?: ReactNode;
  /**
   * When header actions fold into the kebab (only relevant for action-only
   * headers that use the overflow layout):
   *  - `"always"` (default) — fold into the kebab whenever space runs out, at
   *    any width.
   *  - `"whenNarrow"` — only fold below the wide breakpoint (1300px). At 1300px
   *    and up, every action stays visible (icon-collapsed, no kebab folding).
   */
  actionOverflow?: "always" | "whenNarrow" | undefined;
  /**
   * A single prominent primary action (e.g. "Add New Credit Card"), rendered
   * OUTSIDE the ControlBar so a `<Button primary>` keeps its filled style and
   * full size. ControlBar `headerActions` are all-secondary by design; use this
   * for the one main CTA a customer-facing page wants in its header.
   */
  primaryAction?: ReactNode | undefined;
  /** Menu items — auto-wrapped in a Menu kebab in the header. Pass Button children directly. */
  menu?: ReactNode;
  /** Full-width filter/control bar rendered below the title row inside the header. */
  filterBar?: ReactNode;
  /** Fully custom header. When set, title/titleBadge/headerContent/filters/actions/menu are ignored. */
  header?: ReactNode;
  /** Stats bar rendered between the header and the content area. */
  statsBar?: StatEntry[] | undefined;
  /** Operational tasks rendered between stats bar and content (e.g. TaskBar components). */
  tasks?: ReactNode;
  /** Content rendered after tasks, before main content (e.g. banner messages, alerts). */
  afterStats?: ReactNode;
  /** When true, the page icon is replaced with a spinner to indicate a background refresh. */
  refreshing?: boolean | undefined;
  /** When true, children render edge-to-edge with no padding. Use for full-bleed tables/maps. */
  fullBleed?: boolean | undefined;
  /** Overrides the app's default density (from PageChromeProvider) for this page. */
  density?: PageDensity | undefined;
  children: ReactNode;
}

function StatsBar_({ items }: { items: StatEntry[] }) {
  return (
    <CopyableProvider value="reveal">
      {/* shrinks any Badge used as a stat value so badge-valued stats
          don't tower over the numeric ones */}
      <BadgeSizeContext.Provider value="small">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 py-1.5">
          {items.map((entry) => {
            if (isStatDivider(entry)) {
              return (
                <div
                  key={entry.dividerId}
                  className={`h-8 w-px shrink-0 bg-border`}
                />
              );
            }
            const item = entry;
            return (
              <div key={item.statKey ?? String(item.label)} className="min-w-0">
                <div
                  className={`flex items-center gap-1 text-[10px] font-semibold tracking-wide whitespace-nowrap text-content-muted uppercase`}
                >
                  {item.label}
                  {item.labelIcon && (
                    <Icon
                      icon={item.labelIcon}
                      size="xs"
                      {...(item.labelIconColor
                        ? { color: item.labelIconColor }
                        : {})}
                    />
                  )}
                  {item.afterLabel}
                  {item.tooltip && (
                    <Tooltip variant="info" size="sm" content={item.tooltip} />
                  )}
                </div>
                <div
                  className={`mt-0.5 text-xs font-bold whitespace-nowrap text-foreground ${item.maxWidth ? "truncate" : ""}`}
                  {...(item.maxWidth
                    ? { style: { maxWidth: item.maxWidth } }
                    : {})}
                >
                  {item.maxWidth && item.copyValue ? (
                    <Tooltip content={item.copyValue}>
                      <CopyableValue
                        value={formatStatValue(item)}
                        copyValue={item.copyValue}
                        copyOptions={item.copyOptions}
                        label={item.label}
                      />
                    </Tooltip>
                  ) : item.href ? (
                    <StatValueLink_ item={item} />
                  ) : (
                    <CopyableValue
                      value={formatStatValue(item)}
                      copyValue={item.copyValue}
                      copyOptions={item.copyOptions}
                      label={item.label}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </BadgeSizeContext.Provider>
    </CopyableProvider>
  );
}

/** Compact inline stats rendered in the page header, to the left of controls */
function HeaderStats_({
  items,
  showDivider,
}: {
  items: StatEntry[];
  showDivider: boolean;
}) {
  return (
    <CopyableProvider value="reveal">
      <BadgeSizeContext.Provider value="small">
        <div
          className={`flex min-w-0 flex-wrap items-center gap-x-5 gap-y-1 ${showDivider ? "min-h-8 border-r border-border pr-5" : ""}`}
        >
          {items.map((entry) => {
            if (isStatDivider(entry)) {
              return (
                <div
                  key={entry.dividerId}
                  className={`h-6 w-px shrink-0 bg-border`}
                />
              );
            }
            const item = entry;
            return (
              <div
                key={item.statKey ?? String(item.label)}
                className="flex items-baseline gap-1.5"
              >
                <span className="flex items-center gap-1 text-[11px] font-semibold tracking-wide whitespace-nowrap text-content-muted uppercase">
                  {item.label}
                  {item.labelIcon && (
                    <Icon
                      icon={item.labelIcon}
                      size="xs"
                      {...(item.labelIconColor
                        ? { color: item.labelIconColor }
                        : {})}
                    />
                  )}
                  {item.afterLabel}
                  {item.tooltip && (
                    <Tooltip variant="info" size="sm" content={item.tooltip} />
                  )}
                </span>
                <span className="text-sm font-bold whitespace-nowrap text-foreground">
                  {item.href ? (
                    <StatValueLink_ item={item} />
                  ) : (
                    <CopyableValue
                      value={formatStatValue(item)}
                      copyValue={item.copyValue}
                      copyOptions={item.copyOptions}
                      label={item.label}
                    />
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </BadgeSizeContext.Provider>
    </CopyableProvider>
  );
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function Page({
  icon,
  iconColorKey,
  iconColor,
  leadingVisual,
  title,
  pageTitle,
  titleBadge,
  titleWraps,
  description,
  tags,
  headerContent,
  headerStats,
  headerFilters,
  headerActions,
  actionOverflow,
  primaryAction,
  menu,
  filterBar,
  header,
  statsBar,
  tasks,
  afterStats,
  refreshing,
  fullBleed,
  density: densityProp,
  children,
}: PageProps) {
  const chrome = usePageChrome();
  const density = densityProp ?? chrome.density;
  const comfortable = density === "comfortable";
  // Action overflow (fold into kebab) can be limited to narrow viewports: at
  // 1300px and up an "whenNarrow" header shows every action; below it, the
  // kebab folding kicks back in.
  const wideForActions = useMinWidth(1300);
  const foldActions =
    actionOverflow === "whenNarrow" ? !wideForActions : undefined;
  // Horizontal gutter: Page pads itself ("self"), or leaves it to the app's
  // route container ("host", e.g. dashboard's responsive site margin) so the
  // content stays flush with the nav.
  // Comfortable pages cap + center their content in a max-w column (see <main>
  // below), which owns the horizontal padding — so the header/body add none.
  const edgeX = comfortable ? "" : chrome.gutter === "host" ? "" : "px-6";
  const titleClass = comfortable
    ? "text-3xl"
    : titleWraps
      ? "text-base min-[1600px]:text-xl"
      : "text-xl";

  const documentTitle =
    pageTitle ?? (typeof title === "string" ? title : undefined);
  const { resolvedTheme } = useThemeContext();
  const colorFn =
    resolvedTheme === "dark" ? stringToColorPairDark : stringToColorPair;
  const colorKey =
    iconColorKey ?? (typeof title === "string" ? title : undefined) ?? "";
  const derivedColors = useMemo(() => colorFn(colorKey), [colorFn, colorKey]);
  const iconColors = iconColor ?? derivedColors;

  useEffect(() => {
    if (documentTitle) {
      // In local dev, prefix with the checkout's slot (e.g. "[2] ") so the
      // browser tab is identifiable when several slots run at once.
      const slot = devSlotNumber();
      const slotPrefix = slot ? `[${slot}] ` : "";
      document.title = `${slotPrefix}${documentTitle} — ${chrome.appName}`;
    }
  }, [documentTitle, chrome.appName]);

  const hasControls =
    !!headerFilters || !!headerActions || !!menu || !!primaryAction;
  // Overflow-into-kebab is for action buttons. Headers with filter controls
  // (dropdowns) must keep wrapping instead — forcing them inline (flex-1,
  // nowrap) squishes the dropdowns. So only use the overflow layout for
  // action-only headers (e.g. shipment, company); filter headers stay wrappable.
  const overflowHeader = !!headerActions && !headerFilters;

  // The page swaps Hubble's grey page wash for dashboard's very light blue. White
  // panels (with their border + shadow) lift off the tint, the way Cargo pages do.

  return (
    <div className={`min-h-full bg-background`}>
      <main
        {...(comfortable ? { className: "mx-auto w-full max-w-5xl px-8" } : {})}
      >
        {header ??
          (title && (
            <div
              className={
                // The header/body separator belongs to the header. When there
                // is a meta band below (stats/tags/tasks) it carries the
                // hairline instead, so the header only draws its own when
                // there's no meta band — otherwise the line would double up.
                // In compact density the title row keeps equal padding above
                // and below either way: the header has its own background, so
                // uneven padding shows as a lopsided band.
                `bg-card ${edgeX} ${
                  statsBar || tags || tasks || afterStats
                    ? comfortable
                      ? "pt-16 pb-2"
                      : "py-3"
                    : comfortable
                      ? "border-b border-border pt-16 pb-6"
                      : "border-b border-border py-3"
                }`
              }
            >
              <div
                className={`flex flex-wrap ${comfortable ? "items-start" : "items-center"} justify-between gap-x-4 gap-y-3`}
              >
                <div className="flex min-w-0 shrink flex-wrap items-center gap-x-4 gap-y-2">
                  {leadingVisual ? (
                    <div className="relative flex shrink-0 items-center">
                      {leadingVisual}
                      {refreshing && (
                        <div className="absolute -top-1 -right-1 rounded-full bg-card text-content-faint">
                          <Spinner size={14} />
                        </div>
                      )}
                    </div>
                  ) : (
                    icon && (
                      <div
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                        style={{
                          backgroundColor: iconColors.background,
                          color: iconColors.foreground,
                        }}
                      >
                        {refreshing ? (
                          <Spinner size={24} onColor />
                        ) : (
                          <Icon icon={icon} size="lg" />
                        )}
                      </div>
                    )
                  )}
                  <div
                    className={
                      // titleWraps: below 1600px the title yields — it shrinks,
                      // wraps, and is capped at a max-width so it can't eat the
                      // room the header actions need; at 1600px and up it snaps
                      // back to a fixed, single-line, full-size title.
                      titleWraps
                        ? "max-w-[15rem] min-w-0 shrink min-[1600px]:max-w-none min-[1600px]:shrink-0"
                        : "shrink-0"
                    }
                  >
                    <div
                      className={`flex items-center gap-2${titleWraps ? " min-w-0" : ""}`}
                    >
                      <h1
                        className={`${titleClass} font-bold tracking-tight text-foreground${titleWraps ? " min-w-0 break-words min-[1600px]:break-normal" : ""}`}
                        style={headingFontStyle()}
                      >
                        {title}
                      </h1>
                      {titleBadge && (
                        <span className="text-base">{titleBadge}</span>
                      )}
                    </div>
                    {/* Compact keeps the description tucked under the title in
                        the left block. Comfortable renders it full-width below
                        the title/action row (see below) so the action tops with
                        the title, matching the customer-page layout.

                        max-w-3xl: this block is a shrink-0 flex item, so its
                        base width is the MAX-CONTENT of its children — an
                        uncapped description therefore renders as one long line
                        that widens the whole document past the viewport
                        (Chromatic then captures the full scrollWidth, scaling
                        every snapshot down). Capping at a readable measure
                        makes long descriptions wrap instead. */}
                    {!comfortable && description && (
                      <p className="mt-0.5 max-w-3xl text-sm text-content-secondary">
                        {description}
                      </p>
                    )}
                  </div>
                  {headerContent && (
                    <>
                      <div className={`h-8 w-px shrink-0 bg-border`} />
                      {/* min-w-0 + overflow-hidden so the breadcrumb clips
                          instead of overrunning the action buttons on the right
                          when the header runs out of width. */}
                      <div className="flex min-w-0 shrink overflow-hidden">
                        {headerContent}
                      </div>
                    </>
                  )}
                </div>
                {(headerStats || hasControls) && (
                  // min-w-0 + flex-wrap so a content-heavy header (stats +
                  // filters + a wide action group) wraps its controls onto a
                  // second line instead of overflowing off-screen. shrink-0
                  // here meant the whole right side could exceed the viewport
                  // width and spill past the edge.
                  //
                  // When stats ride inside the overflow cluster, reserve a floor
                  // width (instead of min-w-0) so the cluster never collapses to
                  // ~0 and spills its stats LEFT over the breadcrumb — the
                  // breadcrumb yields/truncates instead. The overflow bar keeps
                  // its own min-w-0 internally, so action folding still measures
                  // correctly within whatever width this block is given.
                  <div
                    className={
                      overflowHeader
                        ? headerStats
                          ? "flex min-w-[15rem] flex-1 items-center justify-end"
                          : "flex min-w-0 flex-1 items-center justify-end"
                        : "flex min-w-0 flex-wrap items-center justify-end gap-x-5 gap-y-2"
                    }
                  >
                    {/* In overflow mode the stats ride inside the ControlBar as
                        a non-collapsing `leading` block, so [stats | divider |
                        actions | kebab] pack together against the right edge
                        (free space sits to the LEFT of the cluster) and the
                        overflow bar still measures the full width to fold
                        actions into the kebab. Other modes keep stats as a
                        sibling that wraps with the controls. */}
                    {headerStats && !overflowHeader && (
                      <HeaderStats_
                        items={headerStats}
                        showDivider={hasControls}
                      />
                    )}
                    <ControlBar
                      filters={headerFilters}
                      actions={headerActions}
                      menu={menu}
                      actionCollapse={overflowHeader ? "always" : "responsive"}
                      foldActions={foldActions}
                      leading={
                        overflowHeader && headerStats ? (
                          <HeaderStats_
                            items={headerStats}
                            showDivider={hasControls}
                          />
                        ) : undefined
                      }
                    />
                    {/* Outside the ControlBar so a `primary` button keeps its
                        filled style + full size (ControlBar forces secondary). */}
                    {primaryAction}
                  </div>
                )}
              </div>
              {comfortable && description && (
                <p className="mt-2 max-w-2xl text-sm text-content-secondary">
                  {description}
                </p>
              )}
              {filterBar && (
                <div
                  className={
                    // no rule above the filter bar — the header's own
                    // bottom hairline (below the whole header, incl. these
                    // filters) is the single header→body separator.
                    "mt-3 pt-1"
                  }
                >
                  <ControlBar filters={filterBar} />
                </div>
              )}
            </div>
          ))}
        <div
          className={`flex flex-col ${fullBleed ? "" : `${edgeX} ${comfortable ? "pb-8" : "pb-6"}`}`}
        >
          {/* Meta content (stats/tags/tasks/banners) always keeps the page
              gutter — fullBleed only applies to the main `children` slot
              where edge-to-edge tables and maps live. */}
          {(statsBar || tags || tasks || afterStats) && (
            // the title row + stats form a header band, closed off from the
            // content below by a bottom hairline (the page is white, so this
            // border is what anchors the header). It stays inside the page
            // gutter so the hairline obeys the side padding rather than bleeding
            // to the page edges.
            <div
              className={
                fullBleed
                  ? `${edgeX} py-1`
                  : // Tight top pad so the stats sit close to the title; the
                    // bottom pad + hairline give the larger separation before
                    // the body. When every slot renders nothing (e.g. a page
                    // with no stickies), the band takes no space, so the
                    // hairline sits right under the header as on a page with
                    // no band.
                    "border-b border-border pt-0.5 pb-3 [&:not(:has(>:not(:empty)))]:py-0"
              }
            >
              {statsBar && <StatsBar_ items={statsBar} />}
              {tags && (
                <div className="mt-2 flex flex-wrap gap-1.5 empty:hidden">
                  {tags}
                </div>
              )}
              {tasks && (
                <div className="mt-4 flex flex-col gap-3 empty:hidden">
                  {tasks}
                </div>
              )}
              {afterStats && (
                <div className="mt-4 flex flex-col gap-3 empty:hidden">
                  {afterStats}
                </div>
              )}
            </div>
          )}
          {/* The body always sits a consistent gap below whatever precedes it —
              the header's hairline, or the meta band's hairline when stats/tags
              are present — so the first panel never butts the divider. Keying
              this gap to the presence of a stats band (as it once did) made bare
              Panel pages flush against the header. fullBleed content stays
              edge-to-edge by design. */}
          <div className={fullBleed ? "" : "mt-3 flex flex-col gap-4"}>
            <PageEdgeContext.Provider value={!!fullBleed}>
              {children}
            </PageEdgeContext.Provider>
          </div>
        </div>
      </main>
    </div>
  );
}
